/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { Dispatch, SetStateAction } from 'react';
import React, { useEffect, useState } from 'react';
import type { OptionProps, OptionTypeBase } from 'react-select';
import type { AllColorType, AllIconType, OptionType, YearGroupAllowNullOption, YearGroupOption } from '../../util/types';
import type { TagsGet } from '@whizz/api-model';
import { useI18n } from '@whizz/react-i18n';
import styled from 'styled-components';
import { ChoiceGroup, Selector } from '../../components';
import { CustomOptionWithIcon } from '../../components/selector/components';
import { GLOBAL_ICONS, PRODUCT_FRUITS_TOURS, QUERIES_IDS } from '../../util/vars';
import { RosterManagementStudentActionEnum } from '@whizz/api-model';
import { useQuery } from 'react-query';
import { repository } from '@whizz/api-client';
import Input from '../../components/input/Input';
import { debounce } from 'lodash';
import { YearGroupSelector } from '../../pages/RosterManagement/modules';
import { isProductFruitsReady, useProductFruitsApi } from 'react-product-fruits';
import { useCleverIntegration } from '../useCleverIntegration';
import useYearGroups from '../useYearGroups';

type FilterType = 'students' | 'yearGroups' | 'classes' | 'tags' | 'active';
type ActionType = {
  value: RosterManagementStudentActionEnum,
  label: string,
  icon: AllIconType,
  color?: AllColorType,
  filtersEnabled?: boolean | FilterType[],
  disableIfClever: boolean;
};

const actions: ActionType[] = [
  {
    label: 'EDIT_STUDENT',
    value: RosterManagementStudentActionEnum.edit,
    icon: GLOBAL_ICONS.edit,
    filtersEnabled: true,
    disableIfClever: false,
  },
  {
    label: 'MOVE_STUDENT',
    value: RosterManagementStudentActionEnum.moveToClass,
    icon: 'TrendingFlat',
    disableIfClever: true,
  },
  {
    label: 'CHANGE_STUDENT_YEAR_GROUP',
    value: RosterManagementStudentActionEnum.moveToYearGroup,
    icon: 'TrendingFlat',
    disableIfClever: true,
  },
  {
    label: 'ADD_STUDENT_TO_EXTRA_CLASS',
    value: RosterManagementStudentActionEnum.addToExtraClass,
    icon: GLOBAL_ICONS.add,
    filtersEnabled: true,
    disableIfClever: true,
  },
  {
    label: 'REMOVE_STUDENT',
    value: RosterManagementStudentActionEnum.removeFromClass,
    icon: GLOBAL_ICONS.collapse_on,
    disableIfClever: true,
  },
  {
    label: 'ACTIVATE_STUDENT',
    value: RosterManagementStudentActionEnum.activate,
    icon: 'Lightbulb',
    color: 'secondary',
    filtersEnabled: [ 'students', 'yearGroups', 'classes', 'tags' ],
    disableIfClever: false,
  },
  {
    label: 'DEACTIVATE_STUDENT',
    value: RosterManagementStudentActionEnum.deactivate,
    icon: 'LightbulbOff',
    filtersEnabled: [ 'students', 'yearGroups', 'classes', 'tags' ],
    disableIfClever: false,
  },
  {
    label: 'ADD_TAGS',
    value: RosterManagementStudentActionEnum.addTags,
    icon: 'Label',
    filtersEnabled: true,
    disableIfClever: false,
  },
  {
    label: 'REMOVE_TAGS',
    value: RosterManagementStudentActionEnum.removeTags,
    icon: 'LabelOutlined',
    filtersEnabled: true,
    disableIfClever: false,
  },
  // {
  //   label: 'Set usage cap',
  //   value: RosterManagementStudentActionEnum.setUsageCap,
  //   icon: 'LockClock',
  // },
  {
    label: 'ARCHIVE_STUDENT',
    value: RosterManagementStudentActionEnum.archive,
    icon: 'Archive',
    filtersEnabled: true,
    disableIfClever: true,
  },
  {
    label: 'UNARCHIVE_STUDENT',
    value: RosterManagementStudentActionEnum.unarchive,
    icon: 'Unarchive',
    filtersEnabled: [ 'students', 'yearGroups', 'tags' ],
    disableIfClever: true,
  },
  {
    label: 'DELETE_ARCHIVED_STUDENT',
    value: RosterManagementStudentActionEnum.completelyDeleteArchived,
    icon: GLOBAL_ICONS.delete,
    disableIfClever: true,
  },
];

const CustomOption = (props: OptionProps<OptionTypeBase, false>) => {
  return (
    <CustomOptionWithIcon
      icon={props.data.icon}
      align={'left'}
      size={30}
      color={props.data.color}
      persistColor={!!props.data.color}
      {...props}
    >
      {props.children}
    </CustomOptionWithIcon>
  );
};

interface SelectorProps {
  label: string;
  value: OptionType<number> | null;
  hideLabel?: boolean;
  placeholder?: string;
  onChange: (value: OptionType<number>) => void;
}

const ActiveSelector = (
  {
    value,
    onChange,
  }: Pick<SelectorProps, 'value' | 'onChange'>) => {
  const { translate } = useI18n();

  const options = [
    {
      value: 1,
      label: translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.ACTIVE'),
    },
    {
      value: 0,
      label: translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.INACTIVE'),
    },
  ];

  return (
    <Selector
      id={'active'}
      label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.ACTIVE')}
      placeholder={translate('components.SELECTORS.PLACEHOLDERS.FILTER')}
      options={options}
      value={value}
      hideLabel
      isClearable
      onChange={(selectedOption: OptionType<number>) => onChange(selectedOption ?? null)}
    />
  );
};

const ClassSelector = (
  {
    label,
    options,
    value,
    hideLabel,
    placeholder,
    onChange,
  }: SelectorProps & { options: OptionType[] }) => {
  const { translate } = useI18n();
  return (
    <Selector
      id={'class'}
      label={label}
      placeholder={placeholder ?? translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.SELECT_CLASS')}
      options={options}
      value={value}
      hideLabel={hideLabel}
      onChange={(selectedOption: OptionType<number>) => onChange(selectedOption ?? null)}
    />
  );
};

const TagSelector = (
  {
    label,
    options,
    value,
    hideLabel,
    placeholder,
    onChange,
  }: SelectorProps & { label?: string, options: OptionType<number>[] } ) => {
  const { translate } = useI18n();
  return (
    <Selector
      id={'tags'}
      label={label}
      placeholder={placeholder ?? translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.SELECT_TAG')}
      options={options}
      value={value}
      hideLabel={hideLabel}
      onChange={(selectedOption: OptionType<number>) => onChange(selectedOption ?? null)}
    />
  );
};

const YearGroupCheck = (
  {
    yearGroupId,
    checked,
    onChange,
  }: { yearGroupId?: number | null, checked: boolean, onChange: (value: boolean) => void },
) => {
  const { translate } = useI18n();

  if (null == yearGroupId) {
    return null;
  }

  return (
    <div className={'col-span-3 mt-6'}>
      <ChoiceGroup
        id={'move_year'}
        name={'move_year'}
        label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.ALSO_MOVE', { year: translate(`year_groups.${yearGroupId}`) })}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
    </div>
  );
};

const SubAction = (
  {
    action,
    classesOptions,
    yearGroupsOptions,
    tagsOptions,
    extraData,
    setExtraData,
  }: {
    action: RosterManagementStudentActionEnum | null,
    classesOptions: ClassOptionType[],
    yearGroupsOptions: FiltersProps['yearGroupsOptions'],
    tagsOptions: OptionType<number>[],
    extraData: ExtraDataType,
    setExtraData: Dispatch<SetStateAction<ExtraDataType>>,
  }): JSX.Element | null => {
  const { translate } = useI18n();
  const onChangeHandler = (prop: keyof ExtraDataType) => (value: OptionType | YearGroupOption | YearGroupAllowNullOption | boolean) => setExtraData((prev) => ({ ...prev, [prop]: value as OptionType<number> }));

  switch (action) {
    case RosterManagementStudentActionEnum.moveToClass: {
      const selectedToClass = classesOptions.find((option) => option.value === extraData.toClass?.value);

      return (
        <>
          <div className={'col-span-2'}>
            <ClassSelector
              label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.FROM')}
              options={classesOptions}
              value={extraData.class ?? null}
              onChange={onChangeHandler('class')}
            />
          </div>
          <div className={'col-span-2'}>
            <ClassSelector
              label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.TO')}
              options={classesOptions}
              value={extraData.toClass ?? null}
              onChange={onChangeHandler('toClass')}
            />
          </div>
          <div className={'col-span-3'}>
            <YearGroupCheck
              yearGroupId={selectedToClass?.yearGroupId}
              checked={extraData.moveYearGroup}
              onChange={onChangeHandler('moveYearGroup')}
            />
          </div>
        </>
      );
    }
    case RosterManagementStudentActionEnum.moveToYearGroup: {
      return (
        <>
          <div className={'col-span-2'}>
            <YearGroupSelector
              label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.FROM')}
              options={yearGroupsOptions}
              value={extraData.yearGroup?.value ?? ''}
              onChange={onChangeHandler('yearGroup')}
            />
          </div>
          <div className={'col-span-2'}>
            <YearGroupSelector
              label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.TO')}
              options={yearGroupsOptions}
              value={extraData.toYearGroup?.value ?? ''}
              onChange={onChangeHandler('toYearGroup')}
              disableNoYearGroup
            />
          </div>
        </>
      );
    }
    case RosterManagementStudentActionEnum.addToExtraClass:
    case RosterManagementStudentActionEnum.unarchive:
      return (
        <div className={'col-span-3'}>
          <ClassSelector
            label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.TO')}
            options={classesOptions}
            value={extraData.class ?? null}
            onChange={onChangeHandler('class')}
          />
        </div>
      );
    case RosterManagementStudentActionEnum.removeFromClass:
      return (
        <div className={'col-span-3'}>
          <ClassSelector
            label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.FROM')}
            options={classesOptions}
            value={extraData.class ?? null}
            onChange={onChangeHandler('class')}
          />
        </div>
      );
    case RosterManagementStudentActionEnum.addTags:
    case RosterManagementStudentActionEnum.removeTags:
      return (
        <div className={'col-span-3'}>
          <TagSelector
            label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.TAG')}
            options={tagsOptions}
            value={extraData.tag ?? null}
            onChange={onChangeHandler('tag')}
          />
        </div>
      );
    default:
      return null;
  }
};

const StudentSearch = styled(Input)`
  border: 1px solid black;
  border-radius: 8px;
  background: #fff;
  height: 45px;
`;

const Filters = (
  {
    yearGroupsOptions,
    classesOptions,
    tagsOptions,
    extraData,
    setExtraData,
    filtersEnabled,
  }: FiltersProps,
) => {
  const { translate } = useI18n();
  const setExtraDataWithDebounce = debounce(setExtraData, 500);
  const enabledFilter = (filterType: FilterType): boolean => {
    if (typeof filtersEnabled === 'boolean' || filtersEnabled == undefined) {
      return !!filtersEnabled;
    } else {
      return  filtersEnabled.includes(filterType);
    }
  };

  return (
    <>
      <tr role={'row'}>
        <th></th>
        <th>
          { enabledFilter('students') && <StudentSearch
            id={'search_students'}
            key={'search_students'}
            label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.SEARCH')}
            placeholder={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.SEARCH_STUDENT')}
            icon={GLOBAL_ICONS.search}
            iconPosition={'left'}
            onChange={(event) => setExtraDataWithDebounce((prev) => ({ ...prev, search: event.target.value }))}
            onClear={() => setExtraDataWithDebounce((prev) => ({ ...prev, search: '' }))}
            hideLabel
          /> }
        </th>
        <th>
          { enabledFilter('active') && <ActiveSelector
            value={extraData.active ?? null}
            onChange={(value) => setExtraData((prev) => ({ ...prev, active: value }))}
          /> }
        </th>
        <th>
          { enabledFilter('yearGroups') && <YearGroupSelector
            label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.YEAR_GROUP')}
            placeholder={translate('components.SELECTORS.PLACEHOLDERS.FILTER')}
            options={yearGroupsOptions}
            value={extraData.localYearGroup?.value ?? ''}
            onChange={(option) => setExtraData((prev) => ({ ...prev, localYearGroup: option as OptionType<number> }))}
            isClearable
            disableNoYearGroup
            hideLabel
          /> }
        </th>
        <th>
          { enabledFilter('classes') && <ClassSelector
            label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.CLASS')}
            placeholder={translate('components.SELECTORS.PLACEHOLDERS.FILTER')}
            options={classesOptions}
            value={extraData.localClass ?? null}
            onChange={(value) => setExtraData((prev) => ({ ...prev, localClass: value }))}
            hideLabel
          /> }
        </th>
        <th>
          { enabledFilter('tags') && <TagSelector
            label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.TAG')}
            placeholder={translate('components.SELECTORS.PLACEHOLDERS.FILTER')}
            options={tagsOptions}
            value={extraData.localTag ?? null}
            onChange={(value) => setExtraData((prev) => ({ ...prev, localTag: value }))}
            hideLabel
          /> }
        </th>
      </tr>
    </>
  );
};

const EXTRA_DATA_INITIAL_VALUE = {
  class: null,
  toClass: null,
  yearGroup: undefined,
  toYearGroup: undefined,
  tag: null,
  moveYearGroup: false,
  search: null,
  localYearGroup: undefined,
  localClass: null,
  localTag: null,
  active: null,
} as ExtraDataType;

const useStudentActionSelector = (): [ JSX.Element, RosterManagementStudentActionEnum | null, ExtraDataType, (resetAction?: boolean) => void, ActionType['filtersEnabled'], boolean, JSX.Element | null ] => {
  const { translate } = useI18n();
  const [ isClever ] = useCleverIntegration();
  const [ value, setValue ] = useState<RosterManagementStudentActionEnum | null>(null);
  const [ extraData, setExtraData ] = useState<ExtraDataType>({ ...EXTRA_DATA_INITIAL_VALUE });
  const [ menuOpen, setMenuOpen ] = useState<true | null>(null);
  const [ yearGroupsQuery ] = useYearGroups();

  const yearGroupsOptions = yearGroupsQuery.data!.map((yearGroupItem) => ({
    label: translate(`year_groups.${yearGroupItem.id}`),
    value: yearGroupItem.id,
  }));

  const classesQuery = useQuery(
    [ QUERIES_IDS.ROSTER_MANAGEMENT.CLASSES, 'withoutArchived' ],
    () => repository.NewClassRepository.getClasses({ includeArchived: false }),
    {
      placeholderData: [],
    },
  );

  const classesOptions = classesQuery.data!.map((classItem) => ({
    label: classItem.name,
    value: classItem.id,
    yearGroupId: classItem.yearGroupId,
  }));

  const tagsQuery = useQuery<TagsGet>(
    [ QUERIES_IDS.ROSTER_MANAGEMENT.TAGS ],
    () => repository.TagsRepository.getTags(),
    {
      placeholderData: [],
    },
  );

  const tagsOptions = tagsQuery.data!.map((tagItem) => ({ label: translate(`pages.ROSTER_MANAGEMENT.TAGS.DEFAULT_TAGS_ABBREVIATIONS.${tagItem.abbreviation}`, { defaultValue: tagItem.abbreviation }), value: tagItem.id }));

  useEffect(() => {
    resetAll(false);
  }, [ value ]);

  useProductFruitsApi((api) => {
    if (isProductFruitsReady()) {
      api.tours.listen('tour-card-completed', (tourId: number, { currentCardId }: { currentCardId: string }) => {
        if (tourId === PRODUCT_FRUITS_TOURS.students && currentCardId === '60e070af-e6f2-4617-aaaf-04e4b90e77bf') {
          setMenuOpen(true);
        }
      });

      /* For some unknown reason, the staff tour doesn't start when starting it from the previous tour. This is the workaround */
      window.addEventListener('productfruits_card_custom_btn_click', (e: any) => {
        if (e.detail.id === 'rm-start-staff-tour') {
          api.tours.tryStartTour(PRODUCT_FRUITS_TOURS.staff);
        }
      });

      const closeMenu = () => setMenuOpen(null);
      api.tours.listen('tour-finished', closeMenu);
      api.tours.listen('tour-skipped', closeMenu);
    }
  }, [ isProductFruitsReady ]);

  const resetAll = (resetAction = true) => {
    if (resetAction) {
      setValue(null);
    }

    setExtraData({ ...EXTRA_DATA_INITIAL_VALUE });
  };

  const translatedActions = actions.map((action) => {
    return {
      ...action,
      label: translate(`pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.${action.label}`),
      isDisabled: action.disableIfClever ? isClever : false,
    };
  });

  const selectedAction = translatedActions.find((option) => value === option.value) ?? null;

  const isValidFunction = () => {
    if (null == selectedAction?.value) {
      return false;
    }

    switch (selectedAction.value) {
      case RosterManagementStudentActionEnum.addToExtraClass:
      case RosterManagementStudentActionEnum.removeFromClass:
      case RosterManagementStudentActionEnum.unarchive:
        return null != extraData.class?.value;
      case RosterManagementStudentActionEnum.moveToClass:
        return null != extraData.class?.value && null != extraData.toClass?.value && extraData.class.value !== extraData.toClass.value;
      case RosterManagementStudentActionEnum.addTags:
      case RosterManagementStudentActionEnum.removeTags:
        return null != extraData.tag?.value;
      case RosterManagementStudentActionEnum.moveToYearGroup:
        return undefined !== extraData.yearGroup?.value && undefined !== extraData.toYearGroup?.value && extraData.yearGroup.value !== extraData.toYearGroup.value;
    }

    // Default
    return true;
  };

  const isValid = isValidFunction();

  const FiltersComponent = (selectedAction?.filtersEnabled && isValid) ? (
    <Filters
      yearGroupsOptions={yearGroupsOptions}
      classesOptions={classesOptions}
      tagsOptions={tagsOptions}
      extraData={extraData}
      setExtraData={setExtraData}
      filtersEnabled={selectedAction.filtersEnabled}
    />
  ) : null;

  const Component = (
    <>
      <div className={'grid grid-cols-11 items-center gap-x-4'}>
        <div className={'col-span-4'}>
          <Selector
            id={'actions'}
            label={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.TITLE')}
            placeholder={translate('pages.ROSTER_MANAGEMENT.STUDENTS.ACTIONS.SELECT_ACTION')}
            value={selectedAction}
            isSearchable={false}
            onChange={(selectedOption: ActionType | null) => setValue(selectedOption?.value ?? null)}
            options={translatedActions}
            customComponents={{ Option: CustomOption }}
            {...(menuOpen ? { menuIsOpen: true } : null)}
          />
        </div>
        <SubAction
          action={value}
          yearGroupsOptions={yearGroupsOptions}
          classesOptions={classesOptions}
          tagsOptions={tagsOptions}
          extraData={extraData}
          setExtraData={setExtraData}
        />
      </div>
    </>
  );

  return [ Component, value, extraData, resetAll, selectedAction?.filtersEnabled ?? false, isValid, FiltersComponent ];
};

export default useStudentActionSelector;

export interface ExtraDataType {
  class: ClassOptionType | null;
  toClass: ClassOptionType | null;
  yearGroup: YearGroupAllowNullOption | undefined;
  toYearGroup: YearGroupOption | undefined;
  tag: OptionType<number> | null;
  moveYearGroup: boolean;
  search: string | null;
  localYearGroup: YearGroupOption | undefined;
  localClass: ClassOptionType | null;
  localTag: OptionType<number> | null;
  active: OptionType<number> | null;
}

interface ClassOptionType extends OptionType<number> {
  yearGroupId?: number | null;
}

interface FiltersProps {
  yearGroupsOptions: OptionType<number>[];
  classesOptions: OptionType<number>[];
  tagsOptions: OptionType<number>[];
  extraData: ExtraDataType;
  setExtraData: Dispatch<SetStateAction<ExtraDataType>>;
  filtersEnabled: ActionType['filtersEnabled'];
}
